import { createContext, useContext, type JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import type { TakiSdk } from '@taki/sdk';
import { TakiShell } from '@taki/shell';
import { demoRegistry } from './registry.js';

/**
 * Contexte applicatif : expose le SDK et la config aux ecrans.
 *
 * La "cage IA" impose que les ecrans n'accedent JAMAIS a Firebase directement :
 * tout passe par `@taki/sdk`. On partage donc l'instance unique du SDK
 * (initialisee dans main.tsx) via ce contexte, plutot que de la reconstruire.
 */
interface TakiAppContextValue {
  config: TakiConfig;
  sdk: TakiSdk;
}

const TakiAppContext = createContext<TakiAppContextValue | null>(null);

/** Accede au SDK + config ; leve si utilise hors du provider <App>. */
export function useTakiApp(): TakiAppContextValue {
  const ctx = useContext(TakiAppContext);
  if (!ctx) {
    throw new Error('useTakiApp doit etre utilise a l\'interieur de <App>.');
  }
  return ctx;
}

export interface AppProps {
  config: TakiConfig;
  sdk: TakiSdk;
}

/**
 * Racine de l'app `hello`.
 *
 * - Fournit le SDK + la config aux ecrans via le contexte applicatif (la cage
 *   IA : ContactsScreen accede au SDK via `useTakiApp`, jamais Firebase direct).
 * - Monte le `TakiShell` : c'est LUI qui fournit tout le chrome (rail de nav,
 *   compte, auth guard, theme du branding) et monte les apps du registre.
 *
 * Le contexte SDK enveloppe le shell pour rester disponible dans les ecrans
 * d'apps (ContactsScreen) montes a l'interieur des routes du shell.
 */
export function App({ config, sdk }: AppProps): JSX.Element {
  return (
    <TakiAppContext.Provider value={{ config, sdk }}>
      <TakiShell config={config} apps={demoRegistry} />
    </TakiAppContext.Provider>
  );
}

export default App;
